import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-simple-map-screenshoter';
import GeometryService from '../../services/GeometryService';
import { buildGeoespacialInventory } from '../reportCalculations/geoespacialInventory';

const DEFAULT_CENTER = [-15.8, -47.9];
const DEFAULT_ZOOM = 5;

const dashboardCarStyle = {
  color: '#00bfff',
  weight: 2,
  opacity: 0.8,
  fillColor: '#b3e5fc',
  fillOpacity: 0.2,
};

const dashboardSigefStyle = {
  color: '#e53935',
  weight: 2,
  opacity: 0.8,
  fillColor: '#ffcdd2',
  fillOpacity: 0.16,
};

const geosAreaStyle = (disabled = false) => ({
  color: disabled ? '#b0bec5' : '#f9a825',
  weight: 2,
  fillColor: disabled ? '#eceff1' : '#fff9c4',
  fillOpacity: disabled ? 0.3 : 0.35,
});

const geosLayerStyle = (disabled = false) => ({
  color: disabled ? '#90a4ae' : '#0d47a1',
  weight: 2,
  fillColor: disabled ? '#cfd8dc' : '#42a5f5',
  fillOpacity: disabled ? 0.4 : 0.5,
});

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeId(value, fallbackPrefix) {
  const base = value || fallbackPrefix || '';
  return String(base)
    .normalize('NFD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .toLowerCase();
}

function createHiddenContainer(width, height) {
  if (typeof document === 'undefined') return null;

  const container = document.createElement('div');
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  container.style.position = 'absolute';
  container.style.top = '-10000px';
  container.style.left = '-10000px';
  container.style.zIndex = '-1';
  container.style.backgroundColor = '#ffffff';

  document.body.appendChild(container);
  return container;
}

async function captureLeafletMap({ width = 640, height = 400, setup }) {
  const container = createHiddenContainer(width, height);
  if (!container) return null;

  let map;
  let plugin;

  try {
    map = L.map(container, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
    });

    if (typeof setup === 'function') {
      await setup(map);
    }

    plugin = L.simpleMapScreenshoter({
      cropImageByInnerWH: true,
      mimeType: 'image/png',
      hideElementsWithSelectors: ['.leaflet-control-container'],
    }).addTo(map);

    await new Promise((resolve) => {
      map.whenReady(() => {
        window.setTimeout(resolve, 500);
      });
    });

    if (!plugin || !plugin._map || !plugin._map._container) {
      return null;
    }

    const dataUrl = await plugin.takeScreen('image');
    return dataUrl || null;
  } catch (error) {
    console.error('[reportMapSnapshots] Erro ao capturar mapa:', error);
    return null;
  } finally {
    try {
      if (plugin && typeof plugin.remove === 'function') {
        plugin.remove();
      }
      if (map && typeof map.remove === 'function') {
        map.remove();
      }
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
    } catch (cleanupError) {
      // Erros de limpeza não devem quebrar o fluxo de geração de snapshots
      console.warn('[reportMapSnapshots] Erro ao limpar recursos do mapa:', cleanupError);
    }
  }
}

function buildDashboardFeatures(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    return { carFeatures: [], sigefFeatures: [], allFeatures: [] };
  }

  const properties = snapshot.properties || null;
  if (!properties || typeof properties !== 'object') {
    return { carFeatures: [], sigefFeatures: [], allFeatures: [] };
  }

  const propertyData = {
    properties,
    _metadata: snapshot._metadata || {},
  };

  let geometryResult;
  try {
    geometryResult = GeometryService.processPropertyGeometries(propertyData);
  } catch (error) {
    console.error('[reportMapSnapshots] Erro ao processar geometrias para dashboard:', error);
    return { carFeatures: [], sigefFeatures: [], allFeatures: [] };
  }

  const carGeometries = Array.isArray(geometryResult?.carGeometries)
    ? geometryResult.carGeometries
    : [];
  const sigefGeometries = Array.isArray(geometryResult?.sigefGeometries)
    ? geometryResult.sigefGeometries
    : [];

  const carFeatures = carGeometries
    .filter((item) => item && item.geojson)
    .map((item, index) => ({
      type: 'Feature',
      geometry: item.geojson,
      properties: {
        ...(item.metadata || {}),
        source: 'car',
        __index: index,
      },
    }));

  const sigefFeatures = sigefGeometries
    .filter((item) => item && item.geojson)
    .map((item, index) => ({
      type: 'Feature',
      geometry: item.geojson,
      properties: {
        ...(item.metadata || {}),
        source: 'sigef',
        __index: index,
      },
    }));

  const allFeatures = [...carFeatures, ...sigefFeatures];

  return { carFeatures, sigefFeatures, allFeatures };
}

async function generateDashboardSnapshot(snapshot) {
  const { carFeatures, sigefFeatures, allFeatures } = buildDashboardFeatures(snapshot);
  if (!allFeatures || allFeatures.length === 0) return null;

  const dataUrl = await captureLeafletMap({
    width: 640,
    height: 400,
    setup: (map) => {
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          attribution:
            '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        },
      ).addTo(map);

      if (carFeatures.length > 0) {
        L.geoJSON(
          { type: 'FeatureCollection', features: carFeatures },
          {
            style: () => dashboardCarStyle,
          },
        ).addTo(map);
      }

      if (sigefFeatures.length > 0) {
        L.geoJSON(
          { type: 'FeatureCollection', features: sigefFeatures },
          {
            style: () => dashboardSigefStyle,
          },
        ).addTo(map);
      }

      if (allFeatures.length > 0) {
        const layer = L.geoJSON({ type: 'FeatureCollection', features: allFeatures });
        const bounds = layer.getBounds();
        if (bounds && bounds.isValid && bounds.isValid()) {
          map.fitBounds(bounds, { padding: [16, 16] });
        } else {
          map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
        }
      }
    },
  });

  return dataUrl;
}

async function generateGeoespacialCarSnapshots(snapshot) {
  const result = {};
  const { carItems } = buildGeoespacialInventory(snapshot);

  if (!Array.isArray(carItems) || carItems.length === 0) {
    return result;
  }

  const ENV_CARD_ORDER = [
    'ÁREA DO IMÓVEL',
    'APP',
    'RESERVA LEGAL',
    'USO RESTRITO',
    'ÁREA CONSOLIDADA',
    'VEGETAÇÃO NATIVA',
    'ÁREA NÃO CLASSIFICADA',
    'ÁREA DE SERVIDÃO',
    'ÁREA DE POUSIO',
  ];

  for (let index = 0; index < carItems.length; index += 1) {
    const item = carItems[index];
    if (!item) continue;

    const codigoId = normalizeId(item.codigo, `car_${index}`);
    const areaImovelFeatures = Array.isArray(item.areaImovelFeatures)
      ? item.areaImovelFeatures
      : [];

    for (const alias of ENV_CARD_ORDER) {
      const layer =
        alias === 'ÁREA DO IMÓVEL' ? item.areaImovelLayer : item.layers && item.layers[alias];

      const declaredHa =
        layer && layer.declaredHa != null
          ? layer.declaredHa
          : alias === 'ÁREA DO IMÓVEL'
            ? item.areaImovelDeclaradaHa
            : null;
      const calculatedHa = layer && layer.calculatedHa != null ? layer.calculatedHa : null;

      const declaredNum = toNumberOrNull(declaredHa);
      const calculatedNum = toNumberOrNull(calculatedHa);

      const isEnvTheme = alias !== 'ÁREA DO IMÓVEL';
      const isDeclaredZeroOrEmpty = declaredNum === null || declaredNum === 0;
      const isCalculatedZeroOrEmpty = calculatedNum === null || calculatedNum === 0;
      const isDisabled = isEnvTheme && isDeclaredZeroOrEmpty && isCalculatedZeroOrEmpty;

      const areaFeatures = areaImovelFeatures;
      const layerFeatures =
        alias === 'ÁREA DO IMÓVEL'
          ? areaImovelFeatures
          : layer && Array.isArray(layer.features)
            ? layer.features
            : [];

      const areaArr = Array.isArray(areaFeatures) ? areaFeatures : [];
      const layerArr = Array.isArray(layerFeatures) ? layerFeatures : [];
      const allFeatures = [...areaArr, ...layerArr];

      if (!allFeatures.length) continue;

      const aliasId = normalizeId(alias, 'alias');
      const mapId = `geos-car-${codigoId}-${aliasId}`;

      // Gera snapshot sequencialmente para evitar sobrecarga
      const dataUrl = await captureLeafletMap({
        width: 480,
        height: 320,
        setup: (map) => {
          if (areaArr.length > 0) {
            L.geoJSON(
              { type: 'FeatureCollection', features: areaArr },
              {
                style: () => geosAreaStyle(isDisabled),
              },
            ).addTo(map);
          }

          if (layerArr.length > 0) {
            L.geoJSON(
              { type: 'FeatureCollection', features: layerArr },
              {
                style: () => geosLayerStyle(isDisabled),
              },
            ).addTo(map);
          }

          const layer = L.geoJSON({ type: 'FeatureCollection', features: allFeatures });
          const bounds = layer.getBounds();
          if (bounds && bounds.isValid && bounds.isValid()) {
            map.fitBounds(bounds, { padding: [4, 4] });
          } else {
            map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
          }
        },
      });

      if (dataUrl) {
        result[mapId] = dataUrl;
      }
    }
  }

  return result;
}

async function generateGeoespacialSigefSnapshots(snapshot) {
  const result = {};
  const { sigefItems } = buildGeoespacialInventory(snapshot);

  if (!Array.isArray(sigefItems) || sigefItems.length === 0) {
    return result;
  }

  for (let index = 0; index < sigefItems.length; index += 1) {
    const item = sigefItems[index];
    if (!item) continue;

    const codigoId = normalizeId(item.codigo, `sigef_${index}`);
    const baseFeatures = Array.isArray(item.baseFeatures) ? item.baseFeatures : [];
    const parcels = Array.isArray(item.parcels) ? item.parcels : [];

    if (!parcels.length) continue;

    for (let parcelIndex = 0; parcelIndex < parcels.length; parcelIndex += 1) {
      const parcel = parcels[parcelIndex];
      if (!parcel) continue;

      const parcelaId = normalizeId(parcel.parcela, `p_${parcelIndex}`);
      const mapId = `geos-sigef-${codigoId}-parcela-${parcelaId}`;

      const parcelFeatures = Array.isArray(parcel.features) ? parcel.features : [];

      const baseArr = Array.isArray(baseFeatures) ? baseFeatures : [];
      const parcelArr = Array.isArray(parcelFeatures) ? parcelFeatures : [];
      const allFeatures = [...baseArr, ...parcelArr];

      if (!allFeatures.length) continue;

      const dataUrl = await captureLeafletMap({
        width: 480,
        height: 320,
        setup: (map) => {
          if (baseArr.length > 0) {
            L.geoJSON(
              { type: 'FeatureCollection', features: baseArr },
              {
                style: () => geosAreaStyle(false),
              },
            ).addTo(map);
          }

          if (parcelArr.length > 0) {
            L.geoJSON(
              { type: 'FeatureCollection', features: parcelArr },
              {
                style: () => geosLayerStyle(false),
              },
            ).addTo(map);
          }

          const layer = L.geoJSON({ type: 'FeatureCollection', features: allFeatures });
          const bounds = layer.getBounds();
          if (bounds && bounds.isValid && bounds.isValid()) {
            map.fitBounds(bounds, { padding: [4, 4] });
          } else {
            map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
          }
        },
      });

      if (dataUrl) {
        result[mapId] = dataUrl;
      }
    }
  }

  return result;
}

export async function generateAllReportMapSnapshots(snapshot) {
  const snapshots = {};

  try {
    const dashboardDataUrl = await generateDashboardSnapshot(snapshot);
    if (dashboardDataUrl) {
      snapshots['dashboard-main'] = dashboardDataUrl;
    }
  } catch (error) {
    console.error('[reportMapSnapshots] Erro ao gerar snapshot do dashboard:', error);
  }

  try {
    const carSnapshots = await generateGeoespacialCarSnapshots(snapshot);
    Object.assign(snapshots, carSnapshots);
  } catch (error) {
    console.error('[reportMapSnapshots] Erro ao gerar snapshots CAR geoespacial:', error);
  }

  try {
    const sigefSnapshots = await generateGeoespacialSigefSnapshots(snapshot);
    Object.assign(snapshots, sigefSnapshots);
  } catch (error) {
    console.error('[reportMapSnapshots] Erro ao gerar snapshots SIGEF geoespacial:', error);
  }

  return snapshots;
}
